import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik } from 'formik';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import validationSchema from './VehiclesSearch.schema';
import {
  FormDatePickerField,
  FormInputField,
  FormInputFieldType,
  FormSelectField,
} from '../../common/components/formFields';
import { dealersDataMockup } from '../../dealers/mockup/DealersMockup';
import { getVehicleMakeList, getVehicleModelsList } from '../VehicleActions';
import { dataToSelect } from '../../common/helpers';
import DealersSelectField from '../../dealers/forms/DealersSelectField';
import LocationsSelectField from '../../locations/forms/LocationsSelectField';
import { SET_SELECTED_VEHICLE, SET_VEHICLES_SEARCH_PARAMS } from '../VehicleReducer';
import ComponentCard from '../../common/components/cards/ComponentCard';

const initialValues = {
  registrationPlate: '',
  buyerOrganizationId: '',
  buyerOrganizationLocationId: '',
  make: {},
  model: {},
  year: '',
  registeredDateFrom: '',
  registeredDateTo: '',
  sellerOrganizationId: '',
  sellerOrganizationLocationId: '',
  launchDateFrom: '',
  launchDateTo: '',
  endDateFrom: '',
  endDateTo: '',
  search: '',
  launchingCounterSerial: '',
};
const dealers = dealersDataMockup.map((dealer) => ({ value: dealer.id, label: dealer.name }));
let formContext = null;

function VehiclesSearch({ onSearch, dealerName }) {
  const previousSearchParams = useSelector((state) => state.vehicles.get('searchParams'));
  const { dealerId } = useParams();
  const dispatch = useDispatch();

  const formRef = useRef();
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [reloadDatePickerKey, setReloadDatePickerKey] = useState(false);

  if (dealerId) {
    const dealer = dealers.filter((item) => item.value === +dealerId);
    if (dealer.length) {
      [initialValues.dealer] = dealer;
    }
  } else {
    initialValues.dealer = {};
  }

  useEffect(() => {
    if (previousSearchParams?.searchCriteria) {
      formRef.current.setValues(previousSearchParams.searchCriteria);
    }
  }, []);

  useEffect(() => {
    if (dealerName && formRef.current) {
      const option = { value: dealerId, label: dealerName };
      formRef.current.setFieldValue('sellerOrganizationId', option);
    }
  }, [dealerName, dealerId]);

  useEffect(() => {
    dispatch(
      getVehicleMakeList((makes) => {
        setMakes(dataToSelect(makes.data, ['id', 'name']));
      }),
    );
  }, [dispatch]);

  const handlerMakeChange = (make) => {
    if (make?.value) {
      formContext.setFieldValue('model', '');
      dispatch(
        getVehicleModelsList(make.value, (models) => {
          setModels(dataToSelect(models.data, ['id', 'name']));
        }),
      );
    }
  };

  const prepareSearchCriteria = (values, { setSubmitting }) => {
    const params = { ...values };
    if (params?.make?.value) {
      params.makeId = params.make.value;
    }
    if (params?.model?.value) {
      params.modelId = params.model.value;
    }
    onSearch(params, setSubmitting);
  };

  return (
    <Row>
      <Col md="12">
        <ComponentCard title="Search vehicle" isCollapsible>
          <Formik
            innerRef={formRef}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={prepareSearchCriteria}
            validateOnBlur
          >
            {(form) => {
              formContext = form;
              return (
                <Form className="search-form-compact">
                  <Row>
                    <Col md="4">
                      <FormInputField name="registrationPlate" label="VRM" />
                    </Col>
                    <Col md="4">
                      <DealersSelectField
                        name="buyerOrganizationId"
                        label="Buyer"
                        onChangeHandler={() =>
                          form.setFieldValue('buyerOrganizationLocationId', null)
                        }
                      />
                    </Col>
                    <Col md="4">
                      <LocationsSelectField
                        name="buyerOrganizationLocationId"
                        label="Buyer Location"
                        organizationId={form.values.buyerOrganizationId?.value || null}
                        disabled={!form.values.buyerOrganizationId?.value}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md="4">
                      <DealersSelectField
                        name="sellerOrganizationId"
                        label="Seller"
                        disabled={!!dealerName}
                        onChangeHandler={() =>
                          form.setFieldValue('sellerOrganizationLocationId', null)
                        }
                      />
                    </Col>
                    <Col md="4">
                      <LocationsSelectField
                        name="sellerOrganizationLocationId"
                        label="Seller Location"
                        organizationId={form.values.sellerOrganizationId?.value || null}
                        disabled={!form.values.sellerOrganizationId?.value}
                      />
                    </Col>
                    <Col md="4">
                      <FormSelectField
                        name="make"
                        label="Make"
                        options={makes}
                        onChangeHandler={handlerMakeChange}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md="4">
                      <FormSelectField name="model" label="Model" options={models} />
                    </Col>
                    <Col md="4">
                      <FormInputField
                        name="year"
                        label="Year"
                        type={FormInputFieldType.NUMBER}
                      />
                    </Col>
                    <Col md="4">
                      <FormDatePickerField
                        name="registeredDateFrom"
                        label="Registration Date From"
                        key={reloadDatePickerKey}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md="4">
                      <FormDatePickerField
                        name="registeredDateTo"
                        label="Registration Date To"
                        key={reloadDatePickerKey}
                        isUpperRange
                      />
                    </Col>
                    <Col md="4">
                      <FormInputField
                        name="launchingCounterSerial"
                        label="Offer ID"
                        defaultValue=""
                      />
                    </Col>
                    <Col xs={12} sm={12} md={4}>
                      <FormDatePickerField
                        name="launchDateFrom"
                        label="Launch Date From"
                        key={reloadDatePickerKey}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12} sm={12} md={4}>
                      <FormDatePickerField
                        name="launchDateTo"
                        label="Launch Date To"
                        key={reloadDatePickerKey}
                        isUpperRange
                      />
                    </Col>
                    <Col xs={12} sm={12} md={4}>
                      <FormDatePickerField
                        name="endDateFrom"
                        label="End Date From"
                        key={reloadDatePickerKey}
                      />
                    </Col>
                    <Col xs={12} sm={12} md={4}>
                      <FormDatePickerField
                        name="endDateTo"
                        label="End Date To"
                        key={reloadDatePickerKey}
                        isUpperRange
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md="12">
                      <Button
                        className="mt-4 float-end ms-2 rounded-pill btn-primary"
                        type="submit"
                      >
                        Search
                      </Button>
                      <Button
                        className="mt-4 float-end rounded-pill btn-soft-primary"
                        type="button"
                        onClick={() => {
                          if (previousSearchParams) {
                            dispatch({
                              type: SET_SELECTED_VEHICLE,
                            });
                            dispatch({
                              type: SET_VEHICLES_SEARCH_PARAMS,
                              payload: {
                                status: previousSearchParams.status,
                              },
                            });
                          }
                          setReloadDatePickerKey((prevState) => !prevState);
                          form.resetForm();
                          if (dealerName) {
                            const sellerOption = { value: dealerId, label: dealerName };
                            form.setFieldValue('sellerOrganizationId', sellerOption);
                          }
                          form.submitForm();
                        }}
                      >
                        Clear
                      </Button>
                    </Col>
                  </Row>
                </Form>
              );
            }}
          </Formik>
        </ComponentCard>
      </Col>
    </Row>
  );
}

VehiclesSearch.propTypes = {
  onSearch: PropTypes.func.isRequired,
  dealerName: PropTypes.string,
};

VehiclesSearch.defaultProps = {
  dealerName: null,
};

export default VehiclesSearch;
